import React from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import ManualBookingNotice from '../../../client/salonDetails/ManualBookingNotice';
import SalonPublicButton from '../../salonPublicAppearance/SalonPublicButton';
import SalonPublicServiceCard from '../../salonPublicAppearance/SalonPublicServiceCard';
import SalonPublicServices from '../../salonPublicAppearance/SalonPublicServices';
import SalonPublicStyleScope from '../../salonPublicAppearance/SalonPublicStyleScope';


const SERVICE_VARIANTS = new Set([
  'saloone',
  'editorial',
  'cards',
  'price_table',
  'clinical',
  'tiles',
  'menu',
  'experiences',
  'technical',
]);

const SalonTemplateServices = ({
  data = [],
  variant = 'saloone',
  visible = true,
  mode = 'public',
  viewport = 'desktop',
  loading = false,
  onBookService,
  categoryAnchorPrefix,
}) => {
  const { t } = useTranslation();
  const { layout, tokens: theme } = useSalonPublicTheme();
  const serviceVariant = SERVICE_VARIANTS.has(variant) ? variant : 'saloone';
  const isGrid = layout.tokens.content.services === 'grid';
  const priceTable = serviceVariant === 'price_table';
  const technical = serviceVariant === 'technical';
  const tiles = serviceVariant === 'tiles';
  const compactPriceTable = priceTable && viewport === 'mobile';
  const categories = Array.isArray(data) ? data.filter((category) => (
    Array.isArray(category?.services) && category.services.length > 0
  )) : [];
  const preview = mode === 'preview';
  const serviceCardHoverBackground = serviceVariant === 'menu'
    ? theme.salon.surfaceMuted
    : theme.colors.gray[50];

  if (!visible || (!loading && categories.length === 0)) return null;

  const formatPrice = (service, value) => {
    if (value == null) return '';
    const amount = `${value} ${t('landing_salon_details.currency')}`;
    return service.isFixedPrice === false
      ? `${t('landing_salon_details.starting_from')} ${amount}`
      : amount;
  };

  return (
    <SalonPublicStyleScope
      sectionKey="services"
      data-testid="salon-section-services"
      data-salon-template-block="services"
      data-variant={serviceVariant}
      sx={{ mt: 4, '&:first-of-type': { mt: 0 } }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          component="h2"
          data-salon-text-role="title"
          variant="h5"
          sx={{ color: theme.colors.gray[700], fontWeight: 600, mb: 1 }}
        >
          {t('landing_salon_details.our_services')}
        </Typography>
        <Box sx={{ width: 60, height: 3, bgcolor: theme.colors.secondary[400], borderRadius: 2 }} />
      </Box>

      {loading ? (
        <Box data-salon-text-role="content" sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {[1, 2, 3].map((item) => (
            <Box key={item} data-testid="salon-services-loading-skeleton">
              <Skeleton variant="rectangular" width={150} height={32} sx={{ mb: 2, borderRadius: 2 }} />
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          id="services"
          data-testid="salon-public-services"
          data-services={layout.tokens.content.services}
          data-salon-text-role="content"
          sx={{ display: 'flex', flexDirection: 'column', gap: 4, pl: { xs: 0, md: 3 } }}
        >
          {categories.map((category) => (
            <Box
              key={category.id}
              id={categoryAnchorPrefix ? `${categoryAnchorPrefix}-${category.id}` : undefined}
              data-testid={categoryAnchorPrefix ? 'salon-service-category' : undefined}
            >
            {category.name ? (
              <Typography
                component="h3"
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.colors.secondary[700],
                  fontSize: '1.5rem',
                  mb: 3,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  textAlign: layout.tokens.content.alignment,
                }}
              >
                {category.name}
              </Typography>
            ) : null}

            <SalonPublicServices
              data-testid="salon-category-services"
              role={priceTable ? 'list' : undefined}
              sx={layout.tokens.content.services === 'list' ? {
                backgroundColor: theme.colors.background.primary,
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                gap: 0,
              } : undefined}
            >
              {category.services.map((service, index) => {
                const basePrice = formatPrice(service, service.price);
                const discountedPrice = formatPrice(service, service.promotion?.discountedPrice);
                const durationLabel = service.formattedDuration
                  || service.formatted_duration
                  || (service.duration != null && service.duration !== ''
                    ? t('common.minutes_short', { count: service.duration })
                    : '');

                const renderDuration = (compact) => (durationLabel ? (
                  <Box
                    role={priceTable ? 'group' : undefined}
                    aria-label={priceTable
                      ? `${t('client.duration')}: ${durationLabel}`
                      : undefined}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      minWidth: compact ? 0 : '90px',
                    }}
                  >
                    <AccessTimeIcon sx={{
                      fontSize: compact ? '0.875rem' : '1rem',
                      color: theme.colors.gray[400],
                    }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.colors.gray[600],
                        fontSize: compact ? '0.8rem' : '0.9rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {durationLabel}
                    </Typography>
                  </Box>
                ) : null);

                const renderPrice = (compact) => (basePrice ? (
                  <Box
                    data-testid="salon-service-price"
                    role={priceTable ? 'group' : undefined}
                    aria-label={priceTable
                      ? `${t('client.price')}: ${discountedPrice || basePrice}`
                      : undefined}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: discountedPrice ? 0.25 : 0,
                      minWidth: compact
                        ? 0
                        : discountedPrice ? '120px' : '100px',
                      textAlign: 'right',
                    }}
                  >
                    {discountedPrice ? (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: theme.colors.gray[500],
                          textDecoration: 'line-through',
                          textAlign: 'right',
                        }}
                      >
                        {basePrice}
                      </Typography>
                    ) : null}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: discountedPrice ? 900 : 700,
                        color: discountedPrice
                          ? theme.colors.primary[700]
                          : theme.colors.gray[600],
                        fontSize: compact ? '1rem' : '1.1rem',
                        textAlign: 'right',
                      }}
                    >
                      {discountedPrice || basePrice}
                    </Typography>
                  </Box>
                ) : null);

                const renderAction = (compact) => (service.canBook ? (
                  <Box
                    data-testid="salon-service-action"
                    data-service-action-layout={compact ? 'full' : 'desktop'}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: compact ? 'stretch' : 'flex-end',
                      gap: 0.75,
                      width: compact ? '100%' : 'auto',
                      maxWidth: compact ? 'none' : 260,
                    }}
                  >
                    <SalonPublicButton
                      preserveLegacyStyle
                      variant="contained"
                      size="medium"
                      onClick={() => onBookService?.(service.raw)}
                      disabled={preview}
                      aria-disabled={String(preview)}
                      fullWidth={compact}
                      sx={{
                        backgroundColor: theme.salon.primary,
                        color: theme.salon.compatibilityMode ? 'white' : theme.salon.onPrimary,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        px: 3,
                        py: compact ? 1.5 : 1,
                        borderRadius: '8px',
                        width: compact ? '100%' : 'auto',
                        minWidth: compact ? 0 : '120px',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: theme.colors.primary[400],
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {t('landing_salon_details.choose')}
                    </SalonPublicButton>
                    <ManualBookingNotice
                      bookingConfirmationMode={service.bookingConfirmationMode}
                      textAlign={compact ? 'left' : 'right'}
                    />
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.colors.warning[700],
                      fontStyle: 'italic',
                      fontSize: '0.85rem',
                      textAlign: compact ? 'left' : 'right',
                      maxWidth: compact ? 'none' : 260,
                    }}
                  >
                    {t('landing_salon_details.contact_salon_directly')}
                  </Typography>
                ));

                return (
                  <SalonPublicServiceCard
                    key={service.id}
                    role={priceTable ? 'listitem' : undefined}
                    data-testid="salon-services-card"
                    data-service-layout={isGrid ? 'grid' : 'list'}
                    data-list-separator={String(!isGrid && index < category.services.length - 1)}
                    style={{
                      '--salon-service-card-hover-background': serviceCardHoverBackground,
                    }}
                    sx={{
                      position: 'relative',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: { xs: 1.5, md: 2 },
                      p: { xs: 2.5, md: 3 },
                      height: '100%',
                      border: technical || tiles ? `1px solid ${theme.salon.border}` : 'none',
                      borderBottom: !isGrid && index < category.services.length - 1
                        ? `1px solid ${theme.colors.gray[200]}`
                        : 'none',
                      borderRadius: isGrid ? (technical ? 2 : tiles ? 4 : 3) : 0,
                      overflow: isGrid ? 'hidden' : 'visible',
                      backgroundColor: technical || tiles
                        ? theme.salon.surface
                        : theme.colors.background.primary,
                      boxShadow: isGrid && !technical && !tiles
                        ? '0 2px 12px rgba(0,0,0,0.08)'
                        : 'none',
                      transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
                      '&::before': technical ? {
                          content: '""',
                          position: 'absolute',
                          insetInlineStart: 0,
                          top: 0,
                          bottom: 0,
                          width: 4,
                          backgroundColor: theme.salon.primary,
                        } : tiles ? {
                          content: '""',
                          position: 'absolute',
                          insetInlineStart: 0,
                          insetInlineEnd: 0,
                          top: 0,
                          height: 7,
                          backgroundColor: theme.salon.primary,
                        } : undefined,
                      '&:hover': {
                        backgroundColor: 'var(--salon-service-card-hover-background)',
                        borderColor: technical || tiles ? theme.salon.primary : undefined,
                        transform: technical ? 'translateY(-2px)' : tiles ? 'translateY(-5px)' : 'none',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: 1.5, md: 2 },
                        width: '100%',
                      }}
                    >
                      {!priceTable || compactPriceTable ? (
                        <Box
                          data-testid="salon-service-compact-layout"
                          sx={{
                            display: priceTable || isGrid
                              ? 'flex'
                              : { xs: 'flex', md: 'none' },
                            flexDirection: 'column',
                            gap: 1.5,
                            width: '100%',
                          }}
                        >
                        {technical ? (
                          <Typography
                            data-testid="salon-service-protocol-number"
                            variant="overline"
                            sx={{
                              color: theme.salon.primary,
                              fontWeight: 900,
                              letterSpacing: '0.12em',
                              lineHeight: 1,
                            }}
                          >
                            {String(index + 1).padStart(2, '0')}
                          </Typography>
                        ) : null}
                        {tiles ? (
                          <Typography
                            data-testid="salon-service-tile-number"
                            variant="overline"
                            sx={{
                              width: 'fit-content',
                              px: 1.25,
                              py: 0.25,
                              color: theme.salon.secondary,
                              backgroundColor: theme.salon.primary,
                              borderRadius: 999,
                              fontWeight: 900,
                              letterSpacing: '0.12em',
                              lineHeight: 1.5,
                            }}
                          >
                            LAB / {String(index + 1).padStart(2, '0')}
                          </Typography>
                        ) : null}
                        <Typography
                          component="h4"
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: theme.colors.gray[700],
                            fontSize: '1.15rem',
                            textAlign: 'left',
                          }}
                        >
                          {service.name}
                        </Typography>
                        {service.description ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.colors.gray[500],
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                              textAlign: 'left',
                            }}
                          >
                            {service.description}
                          </Typography>
                        ) : null}
                        <Box
                          data-testid="salon-service-summary-row"
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          {renderDuration(true)}
                          {renderPrice(true)}
                        </Box>
                          {renderAction(true)}
                        </Box>
                      ) : null}

                      {!isGrid && (!priceTable || !compactPriceTable) ? (
                        <Box
                          data-testid="salon-service-desktop-layout"
                          sx={{
                            display: priceTable ? 'flex' : { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            gap: 2,
                            width: '100%',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              width: '100%',
                              gap: 3,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                component="h4"
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.colors.gray[700],
                                  fontSize: '1.25rem',
                                  textAlign: 'left',
                                }}
                              >
                                {service.name}
                              </Typography>
                            </Box>
                            {renderDuration(false)}
                            {renderPrice(false)}
                            {renderAction(false)}
                          </Box>
                          {service.description ? (
                            <Box sx={{ maxWidth: 'calc(70% - 1.5rem)', minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.colors.gray[500],
                                  fontSize: '0.875rem',
                                  lineHeight: 1.6,
                                  textAlign: 'left',
                                }}
                              >
                                {service.description}
                              </Typography>
                            </Box>
                          ) : null}
                        </Box>
                      ) : null}
                    </Box>
                  </SalonPublicServiceCard>
                );
              })}
            </SalonPublicServices>
            </Box>
          ))}
        </Box>
      )}
    </SalonPublicStyleScope>
  );
};

export default SalonTemplateServices;
